import { FilterOption, FilterOptionType } from "../filterPopover/FilterPopover.types.ts";
import { CustomFieldFilter, FilterOperatorUi, ListFilterOperator, TextFilterOperator } from "@common/api/filterTypes.ts";

export const filterOperatorsKey = "filterOperators";

/**
 * The operators of every filter in the bar, keyed by filter id.
 *
 * A filter's operator normally rides inside its own entry in the filter blob, but an empty filter has no entry to
 * ride in: every transformXFilterOptionsToBackend skips options without a value. Left at that, emptying a chip
 * silently reset "Does not contain" to "Contains" the moment the operator menu closed. The map is written on every
 * filter change and holds the operator whether or not the filter has a value, which is the same shape useFilterState
 * already persists for the session-storage backed tables.
 */
export function collectFilterOperators(options: Array<FilterOption>): Record<string, FilterOperatorUi> {
    const operators: Record<string, FilterOperatorUi> = {};

    options.forEach((option) => {
        if (option.filterOperator !== undefined) {
            operators[option.id] = option.filterOperator;
        }
    });

    return operators;
}

/**
 * Attaches the operators to a freshly transformed filter blob. Presentation state, so it is stripped again before
 * the blob becomes a query key or a request body, and the key is left off entirely while no filter has an operator
 * so an untouched view keeps comparing equal to the one it was saved from.
 */
export function withFilterOperators<T>(filterData: T, options: Array<FilterOption>): T {
    const operators = collectFilterOperators(options);
    if (filterData === undefined || filterData === null || Object.keys(operators).length === 0) {
        return filterData;
    }

    return { ...(filterData as Record<string, unknown>), [filterOperatorsKey]: operators } as T;
}

export function hydrateFilterOperators(options: FilterOption[], filterData: Record<string, any> | undefined, idToBackendProperty: Record<string, string>) {
    if (!filterData) return;

    const customFieldFilters = filterData.customFieldFilters as CustomFieldFilter[] | undefined;

    options.forEach((option) => {
        if (option.id.startsWith("custom-field-")) {
            const inputFieldId = option.id.replace("custom-field-", "");
            const customFieldValue = customFieldFilters?.find((cf) => cf.inputFieldId === inputFieldId);
            if (customFieldValue && customFieldValue.operator !== undefined) {
                option.filterOperator = wireOperatorToUi(customFieldValue.operator, option.type);
            }
            return;
        }

        const backendProperty = idToBackendProperty[option.id];
        if (!backendProperty) return;

        const filterEntry = filterData[backendProperty];
        if (filterEntry && typeof filterEntry === "object" && "filterOperator" in filterEntry) {
            option.filterOperator = wireOperatorToUi(filterEntry.filterOperator, option.type);
        }
    });

    // A second pass rather than a branch inside the first: the loop above returns early for custom fields and for
    // options with no backend property, and the map has to reach those too. It wins over the per-filter entries
    // because it is the only source that survives a filter being emptied, and the two are written together so they
    // cannot disagree. Views saved before the map existed simply have nothing here and keep their entry operators.
    const storedOperators = filterData[filterOperatorsKey] as Record<string, FilterOperatorUi> | undefined;
    if (!storedOperators) return;

    options.forEach((option) => {
        const storedOperator = storedOperators[option.id];
        if (storedOperator !== undefined) {
            option.filterOperator = storedOperator;
        }
    });
}

function wireOperatorToUi(wireOperator: number, type: FilterOptionType): FilterOperatorUi {
    if (type === FilterOptionType.TextField) {
        return wireOperator === TextFilterOperator.NotContains ? FilterOperatorUi.Negative : FilterOperatorUi.Positive;
    }
    return wireOperator === ListFilterOperator.NotIn ? FilterOperatorUi.Negative : FilterOperatorUi.Positive;
}

export function uiOperatorToWire(uiOperator: FilterOperatorUi | undefined, type: FilterOptionType): number {
    if (type === FilterOptionType.TextField) {
        return uiOperator === FilterOperatorUi.Negative ? TextFilterOperator.NotContains : TextFilterOperator.Contains;
    }
    return uiOperator === FilterOperatorUi.Negative ? ListFilterOperator.NotIn : ListFilterOperator.In;
}
